import { create } from "zustand"
import { persist } from "zustand/middleware"
import { ObjectType, type ObjectStatus } from "@/db/schema"
import type { MenuItem } from "@/features/static-data/useStaticData"
import { preloadPikaIcons } from "@/shared/icons/resolveContainerIcon"
import { getCachedIconStrings } from "@/features/views/loadingPlaceholders"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Progress data for a container (for TimelineIcon in group headers) */
export interface ContainerProgress {
  completed: number
  total: number
  unfinished: number
  status: ObjectStatus
}

interface AncestorTagState {
  /** containerId → tags[] directly assigned to that container */
  containerTagMap: Record<string, string[]>
  /** projectId → areaId (positional, derived from menu sort order) */
  projectToAreaMap: Record<string, string>
  /** containerId → sortIndex (from sidebar menu) */
  containerSortIndex: Record<string, number>
  /** containerId → title (for grouped headers) */
  containerTitles: Record<string, string>
  /** containerId → ObjectType (Project, List, Area) */
  containerTypes: Record<string, ObjectType>
  /** containerId → progress data (for DynamicIcon) */
  containerProgress: Record<string, ContainerProgress>
  /** containerId → custom icon string (undefined when not set) */
  containerIcons: Record<string, string>
  /** Monotonic counter — bumped only when maps actually change */
  version: number

  // ── Actions ──
  rebuildFromMenu: (menuItems: MenuItem[]) => void
}

// ---------------------------------------------------------------------------
// Synchronous pre-hydration from localStorage
// ---------------------------------------------------------------------------

/**
 * Read the Zustand-persisted state synchronously to use as initial values.
 *
 * Zustand v5 persist middleware hydrates asynchronously (even for localStorage).
 * Without this, the store starts with empty defaults for one render frame,
 * causing GroupedTaskList to sort groups incorrectly (all get Infinity) before
 * the real containerSortIndex arrives — producing a visible sort jump.
 */
function _readPersistedState(): Partial<AncestorTagState> {
  try {
    const raw = localStorage.getItem("totodo:ancestor-tags")
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return (parsed?.state as Partial<AncestorTagState>) ?? {}
  } catch {
    return {}
  }
}

const _ps = _readPersistedState()

// Kick off pika SVG imports immediately so they're loaded before first render
if (_ps.containerIcons) {
  preloadPikaIcons(Object.values(_ps.containerIcons))
}
// Also preload any pika icons cached in view placeholders (container headers)
preloadPikaIcons(getCachedIconStrings())

// ---------------------------------------------------------------------------
// Store (persisted to localStorage)
// ---------------------------------------------------------------------------

export const useAncestorTagStore = create<AncestorTagState>()(
  persist(
    (set, get) => ({
      containerTagMap: _ps.containerTagMap ?? {},
      projectToAreaMap: _ps.projectToAreaMap ?? {},
      containerSortIndex: _ps.containerSortIndex ?? {},
      containerTitles: _ps.containerTitles ?? {},
      containerTypes: _ps.containerTypes ?? {},
      containerProgress: _ps.containerProgress ?? {},
      containerIcons: _ps.containerIcons ?? {},
      version: _ps.version ?? 0,

      rebuildFromMenu: (menuItems: MenuItem[]) => {
        // 1. Build new maps from scratch
        const nextTagMap: Record<string, string[]> = {}
        const nextProjectToArea: Record<string, string> = {}
        const nextSortIndex: Record<string, number> = {}
        const nextTitles: Record<string, string> = {}
        const nextTypes: Record<string, ObjectType> = {}
        const nextProgress: Record<string, ContainerProgress> = {}
        const nextIcons: Record<string, string> = {}

        let currentAreaId: string | undefined

        // Items are pre-sorted by sortIndex from useStaticData.
        // Walk in order: when we see an Area, all Projects/Lists below it
        // (until the next Area) belong to that Area.
        for (const item of menuItems) {
          nextTagMap[item.id] = item.tags
          nextSortIndex[item.id] = item.sortIndex
          nextTitles[item.id] = item.title
          nextTypes[item.id] = item.type
          nextProgress[item.id] = {
            completed: item.completedCount,
            total: item.totalCount,
            unfinished: item.unfinishedCount,
            status: item.status,
          }
          if (item.icon) nextIcons[item.id] = item.icon

          if (item.type === ObjectType.Area) {
            currentAreaId = item.id
          } else if (
            currentAreaId &&
            (item.type === ObjectType.Project || item.type === ObjectType.List)
          ) {
            nextProjectToArea[item.id] = currentAreaId
          }
        }

        // 2. Deep-compare — skip set() if nothing changed
        const prev = get()
        if (
          JSON.stringify(prev.containerTagMap) === JSON.stringify(nextTagMap) &&
          JSON.stringify(prev.projectToAreaMap) ===
            JSON.stringify(nextProjectToArea) &&
          JSON.stringify(prev.containerSortIndex) ===
            JSON.stringify(nextSortIndex) &&
          JSON.stringify(prev.containerTitles) === JSON.stringify(nextTitles) &&
          JSON.stringify(prev.containerTypes) === JSON.stringify(nextTypes) &&
          JSON.stringify(prev.containerProgress) ===
            JSON.stringify(nextProgress) &&
          JSON.stringify(prev.containerIcons) === JSON.stringify(nextIcons)
        ) {
          return // nothing changed — no version bump, no re-render
        }

        set({
          containerTagMap: nextTagMap,
          projectToAreaMap: nextProjectToArea,
          containerSortIndex: nextSortIndex,
          containerTitles: nextTitles,
          containerTypes: nextTypes,
          containerProgress: nextProgress,
          containerIcons: nextIcons,
          version: prev.version + 1,
        })
      },
    }),
    {
      name: "totodo:ancestor-tags",
    },
  ),
)

// ---------------------------------------------------------------------------
// Pure function: compute inherited tags for a task
// ---------------------------------------------------------------------------

/**
 * Given a task's parentPath, return the union of:
 *   1. The direct parent container's tags
 *   2. If the parent is a project/list under an area, the area's tags
 *
 * Deduplicates and returns a sorted array.
 */
export function getInheritedTags(
  parentPath: string,
  state: Pick<AncestorTagState, "containerTagMap" | "projectToAreaMap">,
): string[] {
  // parentPath format: "root/{containerId}" or "root" (inbox)
  const segments = parentPath.split("/")
  const containerId = segments[segments.length - 1]
  if (!containerId || containerId === "root") return []

  const containerTags = state.containerTagMap[containerId] ?? []
  const areaId = state.projectToAreaMap[containerId]
  const areaTags = areaId ? (state.containerTagMap[areaId] ?? []) : []

  if (containerTags.length === 0 && areaTags.length === 0) return []
  if (areaTags.length === 0) return containerTags
  if (containerTags.length === 0) return areaTags

  // Union + deduplicate
  const merged = new Set([...containerTags, ...areaTags])
  return Array.from(merged).sort((a, b) => a.localeCompare(b))
}
